import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { containerVariants, fadeUpVariants } from '../lib/motion';
import { HERO_STATS } from '../mock';

function Counter({ value, duration = 1.8 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  // Separate prefix, digits, and suffix to support currency symbols correctly
  const match = value.match(/^([^0-9]*)([0-9]+)([^0-9]*)$/);
  const prefix = match ? match[1] : '';
  const numericValue = match ? parseInt(match[2], 10) : (parseInt(value.replace(/[^0-9]/g, ''), 10) || 0);
  const suffix = match ? match[3] : value.replace(/[0-9]/g, '');

  useEffect(() => {
    if (!inView) return;

    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      // easeOutQuad easing function
      const easeProgress = progress * (2 - progress);
      setCount(Math.floor(easeProgress * numericValue));

      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }, [inView, numericValue, duration]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
}

export default function Hero() {
  return (
    <section className="relative min-h-screen flex flex-col pt-16 pb-12 bg-brand-mint overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-60" />
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-brand-lime/30 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -right-24 w-96 h-96 bg-brand-green/15 rounded-full blur-3xl" />

      {/* Top Area: Badge with 40% top and 60% bottom dynamic spacing inside flex-grow-[5] */}
      <div className="relative w-full flex flex-col items-center flex-grow-[5] min-h-[120px]">
        <div className="flex-grow-[4]" />
        <motion.div 
          variants={fadeUpVariants(25, 0.55)}
          initial="hidden"
          animate="visible"
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 border border-brand-dark/10 text-xs font-medium text-brand-dark 2xl:text-sm 2xl:px-4 2xl:py-1.5"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-brand-green animate-pulse" />
          Powering Mobility Across 15+ Geographies
        </motion.div>
        <div className="flex-grow-[6]" />
      </div>

      {/* Middle: Title & Paragraph (Core Component centered / slightly below center) */}
      <motion.div 
        variants={containerVariants(0.1, 0.05)}
        initial="hidden"
        animate="visible"
        className="relative max-w-7xl mx-auto px-6 lg:px-12 text-center w-full shrink-0 py-4"
      >
        <motion.h1 
          variants={fadeUpVariants(25, 0.6)}
          className="heading-display text-3xl sm:text-5xl md:text-7xl 2xl:text-8xl text-brand-dark leading-[1.05] w-full mx-auto sm:whitespace-nowrap"
        >
          One Platform. <br /> Built for Multi-Vertical Mobility
        </motion.h1>
        
        <motion.p 
          variants={fadeUpVariants(25, 0.65)}
          className="mt-6 text-base md:text-lg 2xl:text-xl text-brand-dark/75 max-w-3xl 2xl:max-w-4xl mx-auto leading-relaxed 2xl:mt-8"
        >
          GearUp for last-mile delivery. Campus Go for campuses. Orbit for urban commute.
        </motion.p>
      </motion.div>

      {/* Bottom Area: Stats centered between the copy block and the bottom of the screen */}
      <div className="relative w-full flex flex-col items-center flex-grow-[4] min-h-[160px] mt-6">
        <div className="flex-grow" />
        <motion.div 
          variants={containerVariants(0.1, 0.05)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="max-w-7xl mx-auto px-6 lg:px-12 w-full"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 2xl:gap-8">
            {HERO_STATS.map(({ icon: Icon, value, label }) => (
              <motion.div 
                key={label}
                variants={fadeUpVariants(25, 0.6)}
                className="bg-brand-lavenderSoft rounded-2xl p-6 text-center border border-brand-dark/5 shadow-sm hover:shadow-lg hover:-translate-y-1 hover:scale-[1.01] transition-all duration-300 2xl:p-8"
              >
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-white flex items-center justify-center text-brand-dark shadow-sm">
                  <Icon size={20} />
                </div>
                <div className="text-3xl md:text-4xl 2xl:text-5xl font-bold heading-display text-brand-dark"><Counter value={value} /></div>
                <div className="text-xs md:text-sm 2xl:text-base text-brand-dark/60 mt-1 2xl:mt-2">{label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
        <div className="flex-grow" />
      </div>
    </section>
  );
}
